import { sanitizeHtml } from '@this+that/utils/sanitizeHtml';

import {
  extractSignatureBlock,
  hasSignatureBlock,
  stripSignatureBlock,
} from './emailSignatureBlock';
import {
  ATTRIBUTE_VALUES,
  DATA_ATTRIBUTE_SELECTORS,
  DATA_ATTRIBUTES,
} from '../hooks/richTextArea/constants';

export interface EditorContentStructure {
  userInput: string;
  quotedContent: string;
  modifiedQuotedContent: string | null;
  fullHTML: string;
}

export const EMPTY_CONTENT_PATTERNS = {
  EMPTY: '',
  PARAGRAPH: '<p></p>',
  PARAGRAPH_BR: '<p><br></p>',
  DIV_BR: '<div><br></div>',
  BR: '<br>',
} as const;

export const hasTemplateContent = (html: string): boolean => {
  return (
    html.includes(DATA_ATTRIBUTES.USER_INPUT_AREA) ||
    html.includes(DATA_ATTRIBUTES.FORWARDED_CONTENT) ||
    html.includes(DATA_ATTRIBUTES.FORWARDED_SEPARATOR)
  );
};

export const extractUserInputFromTemplate = (html: string): string => {
  if (!hasTemplateContent(html)) {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const userInputArea = doc.querySelector(
    DATA_ATTRIBUTE_SELECTORS.USER_INPUT_AREA,
  );

  return userInputArea?.innerHTML || '';
};

export const extractQuotedContent = (html: string): string => {
  if (!hasTemplateContent(html)) {
    return '';
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const forwardedContent = doc.querySelector(
    DATA_ATTRIBUTE_SELECTORS.FORWARDED_CONTENT,
  );
  const forwardedSeparator = doc.querySelector(
    DATA_ATTRIBUTE_SELECTORS.FORWARDED_SEPARATOR,
  );

  if (forwardedContent) {
    const separatorHTML = forwardedSeparator
      ? forwardedSeparator.outerHTML
      : '';
    return `${separatorHTML}${forwardedContent.outerHTML}`;
  }

  return '';
};

export const normalizeWhitespace = (str: string): string =>
  str.replace(/\s+/g, ' ').trim();

const NBSP_ENTITY_PATTERN = /&nbsp;|&#160;|&#xa0;/gi;
const NBSP_CHAR_PATTERN = /\u00a0/g;
const EMPTY_BLOCK_PATTERN =
  /<\s*(p|div)\s*>\s*(?:<\s*br\s*\/?\s*>\s*)?<\s*\/\s*\1\s*>/gi;
const STANDALONE_BREAK_PATTERN = /<\s*br\s*\/?\s*>/gi;
const WHITESPACE_AFTER_TAG_PATTERN = />\s+/g;
const WHITESPACE_BEFORE_TAG_PATTERN = /\s+</g;
const COLLAPSIBLE_WHITESPACE_PATTERN = /\s+/g;

const decodeNbsp = (html: string): string =>
  html.replace(NBSP_ENTITY_PATTERN, ' ').replace(NBSP_CHAR_PATTERN, ' ');

const removeEmptyBlocksAndBreaks = (html: string): string =>
  html.replace(EMPTY_BLOCK_PATTERN, '').replace(STANDALONE_BREAK_PATTERN, '');

const trimWhitespaceAroundTags = (html: string): string =>
  html
    .replace(WHITESPACE_AFTER_TAG_PATTERN, '>')
    .replace(WHITESPACE_BEFORE_TAG_PATTERN, '<');

export const normalizeDraftHtmlForComparison = (html: string): string => {
  const withoutNbsp = decodeNbsp(html);
  const withoutEmptyBlocks = removeEmptyBlocksAndBreaks(withoutNbsp);
  const withoutTagWhitespace = trimWhitespaceAroundTags(withoutEmptyBlocks);

  return withoutTagWhitespace
    .replace(COLLAPSIBLE_WHITESPACE_PATTERN, ' ')
    .trim();
};

export const isContentEmpty = (content: string): boolean => {
  const trimmed = content.trim();
  return (
    trimmed === EMPTY_CONTENT_PATTERNS.EMPTY ||
    trimmed === EMPTY_CONTENT_PATTERNS.PARAGRAPH ||
    trimmed === EMPTY_CONTENT_PATTERNS.PARAGRAPH_BR ||
    trimmed === EMPTY_CONTENT_PATTERNS.DIV_BR ||
    trimmed === EMPTY_CONTENT_PATTERNS.BR
  );
};

const isEmptyIgnoringSignature = (
  content: string,
  hadSignature: boolean,
): boolean =>
  isContentEmpty(content) ||
  (hadSignature && normalizeDraftHtmlForComparison(content) === '');

export const validateEditorContent = (
  rawHtml: string,
  modifiedQuotedContent: string | null,
  requiresUserInput: boolean,
): boolean => {
  const hadSignature = hasSignatureBlock(rawHtml);
  const html = stripSignatureBlock(rawHtml);
  const hasTemplate = hasTemplateContent(html);

  if (!hasTemplate) {
    return !isEmptyIgnoringSignature(html, hadSignature);
  }

  const userInput = extractUserInputFromTemplate(html);
  const hasUserInput = !isEmptyIgnoringSignature(userInput, hadSignature);
  const hasModifiedQuoted = modifiedQuotedContent !== null;

  if (requiresUserInput) {
    return hasUserInput || hasModifiedQuoted;
  }

  return true;
};

export const isValueEmpty = <T>(value: T): boolean => {
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === 'string') {
    const trimmed = stripSignatureBlock(value).trim();

    if (trimmed === '') {
      return true;
    }

    const textContent = trimmed
      .replace(/<br\s*\/?>/gi, '')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();

    return textContent === '';
  }

  return value === null || value === undefined;
};

export const cleanMessageContent = (
  message: string | undefined,
  forwardedMessage?: string,
): string | undefined => {
  if (!message) {
    return message;
  }
  if (forwardedMessage && message === forwardedMessage) {
    return EMPTY_CONTENT_PATTERNS.PARAGRAPH;
  }
  if (hasTemplateContent(message)) {
    return extractUserInputFromTemplate(message);
  }
  return message;
};

export const splitEditorContent = (
  html: string,
): { userInput: string; quotedContent: string } => {
  if (!hasTemplateContent(html)) {
    return { userInput: html, quotedContent: '' };
  }

  const sepIdx = html.indexOf(DATA_ATTRIBUTES.FORWARDED_SEPARATOR);
  const fwdIdx = html.indexOf(DATA_ATTRIBUTES.FORWARDED_CONTENT);

  const candidates = [sepIdx, fwdIdx].filter((i) => i !== -1);
  if (candidates.length === 0) {
    return { userInput: html, quotedContent: '' };
  }

  const splitAttr = Math.min(...candidates);

  let tagStart = splitAttr - 1;
  while (tagStart >= 0 && html[tagStart] !== '<') {
    tagStart--;
  }

  if (tagStart < 0) {
    return { userInput: html, quotedContent: '' };
  }

  const userInput = html.slice(0, tagStart) || EMPTY_CONTENT_PATTERNS.PARAGRAPH;
  const quotedContent = html.slice(tagStart);

  return { userInput, quotedContent };
};

export const reconstructFullHTML = (
  userInput: string,
  quotedContent: string,
): string => {
  if (!quotedContent) {
    return userInput;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(quotedContent, 'text/html');
  const container = doc.createElement('div');

  const userInputArea = doc.createElement('div');
  userInputArea.setAttribute(
    DATA_ATTRIBUTES.USER_INPUT_AREA,
    ATTRIBUTE_VALUES.TRUE,
  );
  userInputArea.innerHTML = userInput;
  container.appendChild(userInputArea);

  const quotedElements = doc.querySelectorAll(
    DATA_ATTRIBUTE_SELECTORS.FORWARDED_CONTENT_OR_SEPARATOR,
  );
  quotedElements.forEach((element) => {
    container.appendChild(element.cloneNode(true));
  });

  return container.innerHTML;
};

export const escapeHtmlText = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const BULLET_LINE_PREFIX = '- ';
const BOLD_RUN_PATTERN = /\*\*([^*]+)\*\*/g;

interface EscapedLineGroup {
  isBulletList: boolean;
  lines: string[];
}

const applyBoldRuns = (escapedText: string): string =>
  escapedText.replace(BOLD_RUN_PATTERN, '<strong>$1</strong>');

const groupEscapedLinesByBulletState = (
  lines: string[],
): EscapedLineGroup[] => {
  const groups: EscapedLineGroup[] = [];

  for (const line of lines) {
    const isBulletLine = line.startsWith(BULLET_LINE_PREFIX);
    const lastGroup = groups[groups.length - 1];

    if (lastGroup && lastGroup.isBulletList === isBulletLine) {
      lastGroup.lines.push(line);
    } else {
      groups.push({ isBulletList: isBulletLine, lines: [line] });
    }
  }

  return groups;
};

const renderBulletListGroup = (lines: string[]): string => {
  const items = lines
    .map((line) => applyBoldRuns(line.slice(BULLET_LINE_PREFIX.length)))
    .map((item) => `<li>${item}</li>`)
    .join('');
  return `<ul>${items}</ul>`;
};

const renderParagraphGroup = (lines: string[]): string =>
  `<p>${lines.map(applyBoldRuns).join('<br>')}</p>`;

const renderEscapedLineGroup = (group: EscapedLineGroup): string =>
  group.isBulletList
    ? renderBulletListGroup(group.lines)
    : renderParagraphGroup(group.lines);

const convertEscapedParagraphChunkToHtml = (escapedChunk: string): string =>
  groupEscapedLinesByBulletState(escapedChunk.split('\n'))
    .map(renderEscapedLineGroup)
    .join('');

export const convertPlainTextToHtml = (text: string): string => {
  const escapedText = escapeHtmlText(text);
  const html = escapedText
    .split(/\n{2,}/)
    .map(convertEscapedParagraphChunkToHtml)
    .join('');

  return sanitizeHtml(html) ?? escapedText;
};

export const replaceUserInputContent = (
  currentHtml: string,
  newUserInputText: string,
): string => {
  const { userInput, quotedContent } = splitEditorContent(currentHtml);
  return reconstructFullHTML(
    `${convertPlainTextToHtml(newUserInputText)}${extractSignatureBlock(userInput)}`,
    quotedContent,
  );
};

const LINE_BREAK = '\n';
const PARAGRAPH_BREAK = '\n\n';
const BLOCK_TAGS = new Set([
  'P',
  'DIV',
  'LI',
  'UL',
  'OL',
  'BLOCKQUOTE',
  'PRE',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
]);

const collectParagraphs = (node: Node, paragraphs: string[]): void => {
  let current = '';
  const flush = () => {
    if (current.trim()) {
      paragraphs.push(current.trim());
    }
    current = '';
  };

  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      current += child.textContent ?? '';
    } else if (child.nodeName === 'BR') {
      current += LINE_BREAK;
    } else if (BLOCK_TAGS.has(child.nodeName)) {
      flush();
      if (child.nodeName === 'LI') {
        paragraphs.push(
          `${BULLET_LINE_PREFIX}${(child.textContent ?? '').trim()}`,
        );
      } else {
        collectParagraphs(child, paragraphs);
      }
    } else {
      current += child.textContent ?? '';
    }
  });
  flush();
};

export const convertHtmlToPlainText = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const paragraphs: string[] = [];
  collectParagraphs(doc.body, paragraphs);
  return paragraphs.join(PARAGRAPH_BREAK);
};
